// Global Instructions Rule Applied!
//
// Typed-date formats for AntD DatePickers.
//
// A DatePicker with a single `format` parses what the user TYPES against that
// one format, strictly: with the app's MM/DD/YYYY display, "4/7/2025",
// "05/06/26" and "2025-04-07" are all rejected and the field snaps back to
// its old value on blur, which reads as "the date field does not accept a
// typed date" (owner report 2026-10-12, the commitment form). AntD accepts
// a LIST of formats - the first one is the display, every one parses typed
// text - so each picker hands its display format through typedDateFormats()
// and takes the variants a person actually types.

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

// Parsing against a format (and a list of formats, strictly) is the
// customParseFormat plugin. AntD's picker extends it on the shared dayjs
// instance too; extending twice is a no-op, so DateInput's own Enter parse
// does not depend on which module loaded first.
dayjs.extend(customParseFormat)

/** Date-only display formats the app uses and the typed variants each accepts. */
const TYPED_VARIANTS: Record<string, string[]> = {
  'MM/DD/YYYY': ['M/D/YYYY', 'MM/DD/YY', 'M/D/YY', 'MMDDYYYY', 'YYYY-MM-DD', 'YYYY-M-D', 'YYYYMMDD'],
  'YYYY-MM-DD': ['YYYY-M-D', 'YYYYMMDD', 'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY', 'MMDDYYYY']
}

/**
 * The format list for a DatePicker: the display format first, then the
 * typed variants it should also accept. A format that is not a plain
 * date-only string (a function, a list, a date-time format, something
 * unknown) is returned as given.
 */
export function typedDateFormats<T>(format: T): T | string[] {
  if (typeof format !== 'string') return format
  const variants = TYPED_VARIANTS[format]
  return variants ? [format, ...variants] : format
}
